import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useIsMobile,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AdminDevicesListParams } from '@repo/api'
import type { SortState } from '@/components/shared/sortable-table-head'

import { ListPagination } from '@/components/shared/list-pagination'
import { toggleSort } from '@/components/shared/sortable-table-head'
import { DeviceCard } from '@/features/admin/devices/components/device-card'
import { DevicesTable } from '@/features/admin/devices/components/devices-table'
import { useAdminDevicesPaginated } from '@/features/admin/devices/hooks/use-admin-devices'

export const Route = createFileRoute('/admin/devices/')({
  component: AdminDevicesListPage,
})

const ALL = 'all'
type AssignFilter = 'all' | 'assigned' | 'unassigned' | 'noVehicle'
type SortKey = NonNullable<AdminDevicesListParams['sort']>

const isSortKey = (key: string): key is SortKey =>
  ['deviceIdent', 'name', 'createdAt'].includes(key)

function AdminDevicesListPage() {
  const { t } = useTranslation(['admin', 'common'])
  const isMobile = useIsMobile()

  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, { wait: 300 })
  const [assignFilter, setAssignFilter] = useState<AssignFilter>('all')
  const [sort, setSort] = useState<SortState>({
    key: 'createdAt',
    direction: 'desc',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const sortKey: SortKey = sort && isSortKey(sort.key) ? sort.key : 'createdAt'

  const { data, isPending, isFetching } = useAdminDevicesPaginated({
    page,
    pageSize,
    search: debouncedSearch.trim() || undefined,
    unassigned: assignFilter === 'unassigned' ? true : undefined,
    hasVehicle:
      assignFilter === 'assigned'
        ? true
        : assignFilter === 'noVehicle'
          ? false
          : undefined,
    sort: sortKey,
    sortDir: sort?.direction ?? 'desc',
  })

  const handleSort = (key: string) => {
    setSort((prev) => toggleSort(prev, key))
    setPage(1)
  }

  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const hasFilters =
    debouncedSearch.trim().length > 0 || assignFilter !== ALL

  return (
    <div className="space-y-4">
      <Card className="bg-background">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>{t('devices.list.title')}</CardTitle>
              <CardDescription>
                {totalCount} {t('devices.list.totalCount')}
              </CardDescription>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full md:w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder={t('devices.list.searchPlaceholder')}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(1)
                  }}
                  className="h-11 pl-9"
                />
              </div>

              <Select
                value={assignFilter}
                onValueChange={(v) => {
                  setAssignFilter(v as AssignFilter)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-11 w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('devices.list.filters.all')}
                  </SelectItem>
                  <SelectItem value="assigned">
                    {t('devices.list.filters.assigned')}
                  </SelectItem>
                  <SelectItem value="noVehicle">
                    {t('devices.list.filters.noVehicle')}
                  </SelectItem>
                  <SelectItem value="unassigned">
                    {t('devices.list.filters.unassigned')}
                  </SelectItem>
                </SelectContent>
              </Select>

              <Button asChild size="lg" className="primary-bg-gradient">
                <Link to="/admin/devices/create">
                  <Plus className="size-4" />
                  {t('devices.list.create')}
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isPending ? (
            isMobile ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-28 w-full rounded-md" />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border">
                <Skeleton className="h-10 w-full rounded-none" />
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-14 w-full rounded-none border-t"
                  />
                ))}
              </div>
            )
          ) : items.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground text-sm">
                {hasFilters
                  ? t('devices.list.noMatches')
                  : t('devices.list.empty')}
              </p>
            </div>
          ) : (
            <div
              className={isFetching ? 'opacity-60 transition-opacity' : undefined}
            >
              {isMobile ? (
                <div className="space-y-2">
                  {items.map((d) => (
                    <DeviceCard key={d.id} device={d} />
                  ))}
                </div>
              ) : (
                <DevicesTable
                  devices={items}
                  sort={sort}
                  onSort={handleSort}
                />
              )}

              <ListPagination
                page={data!.page}
                totalPages={data!.totalPages}
                pageSize={data!.pageSize}
                totalCount={data!.totalCount}
                hasPrevious={data!.hasPrevious}
                hasNext={data!.hasNext}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
                className="mt-4"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
